// Web shim for `react-native-passkeys`.
//
// The native module exposes `create`, `get`, `isSupported`. None of those work
// in a browser, but the browser has WebAuthn directly — and the legacy web
// (frontend/client) already uses `@passwordless-id/webauthn` against the same
// backend.  This shim wraps that library so `mobile/src/utilities/passkeys.ts`
// works unchanged on desktop web / Electron renderer.
//
// Shape contract: the backend (`backend/api/client/controllers/users/
// {store,verify}-credential/index.js`) verifies registration + authentication
// with `server.verifyRegistration` / `server.verifyAuthentication` from the
// same library — so the JSON we produce here round-trips natively. The
// server's CBOR-decode fallback path (which exists for react-native-passkeys'
// leaner attestation payload) is harmless when our output already includes
// `publicKey` / `publicKeyAlgorithm` — it short-circuits the extraction.
//
// Why this is needed at all: the previous alias in webpack.config.js pointed
// `react-native-passkeys` at `stubs/empty.js` (`export default {}`), so
// `mod.create / mod.get / mod.isSupported` were all `undefined` on web. That
// quietly disabled the entire LockScreen biometric path on `development.eleplan.org`
// despite the browser supporting Touch ID through WebAuthn.

import { client } from '@passwordless-id/webauthn';

// Map react-native-passkeys's `attestation: 'none' | 'direct' | 'indirect'`
// to @passwordless-id/webauthn's boolean.  The library only cares whether to
// surface the attestation object back to the JS caller; the underlying
// WebAuthn create() always receives an `attestation` preference.
function toAttestationBool(attestation) {
  if (typeof attestation === 'string') return attestation !== 'none';
  return !!attestation;
}

// Extract the credential-ID string from either a CredentialDescriptor object
// (react-native-passkeys's `[{ id, type: 'public-key' }]` form) or a raw
// string.  @passwordless-id/webauthn accepts either, but our caller always
// sends descriptors so we normalize at the boundary to keep one shape.
function normalizeAllowCredentials(allowCredentials) {
  if (!Array.isArray(allowCredentials)) return undefined;
  return allowCredentials
    .map((c) => (typeof c === 'string' ? c : c?.id))
    .filter((id) => typeof id === 'string' && id.length > 0);
}

/**
 * Register a new credential.  Matches `react-native-passkeys`'s `create()`
 * signature so `passkeys.ts` doesn't need a web/native branch.
 *
 * Input  — react-native-passkeys / WebAuthn create options:
 *   { rp, user: { id, name, displayName }, challenge, pubKeyCredParams,
 *     authenticatorSelection: { userVerification, residentKey },
 *     attestation, timeout }
 * Output — @passwordless-id/webauthn RegistrationJSON:
 *   { id, rawId, response: { clientDataJSON, attestationObject,
 *     authenticatorData, publicKey, publicKeyAlgorithm, transports },
 *     authenticatorAttachment, clientExtensionResults, type }
 *
 * The `rp` and `pubKeyCredParams` inputs are intentionally ignored — the
 * library derives `rp` from `window.location` (which is what the browser
 * requires anyway; passing a bare `app.eleplan.com` from
 * `development.eleplan.org` is the exact bug that motivated the runtime
 * `getRuntimeRpId()` helper) and hard-codes ES256 + RS256 keyparams to match
 * what every passkey-capable platform authenticator advertises.
 */
export function create(opts) {
  const userVerification =
    opts?.authenticatorSelection?.userVerification || 'required';
  const discoverable = opts?.authenticatorSelection?.residentKey;
  return client.register({
    challenge: opts.challenge,
    user: opts.user,
    userVerification,
    attestation: toAttestationBool(opts?.attestation),
    timeout: opts?.timeout ?? 60000,
    ...(discoverable ? { discoverable } : {}),
  });
}

/**
 * Authenticate against a previously-registered credential.  Matches
 * `react-native-passkeys`'s `get()` signature.
 *
 * Input  — react-native-passkeys / WebAuthn get options:
 *   { challenge, rpId, allowCredentials: [{ id, type: 'public-key' }],
 *     userVerification, timeout }
 * Output — @passwordless-id/webauthn AuthenticationJSON:
 *   { id, rawId, response: { clientDataJSON, authenticatorData,
 *     signature, userHandle }, authenticatorAttachment,
 *     clientExtensionResults, type }
 *
 * `rpId` is dropped for the same reason as in `create()` — the browser pins
 * it to the page origin.  `passkeys.ts` builds the `allowCredentials` list
 * from passkeys already filtered by `pk.origin === currentOrigin`, so any
 * mismatched-origin keys are gone before they reach us.
 */
export function get(opts) {
  return client.authenticate({
    challenge: opts.challenge,
    allowCredentials: normalizeAllowCredentials(opts.allowCredentials),
    userVerification: opts?.userVerification || 'required',
    timeout: opts?.timeout ?? 60000,
  });
}

/**
 * Browser passkey availability.  `client.isAvailable()` returns true when
 * `window.PublicKeyCredential` exists — true in every modern browser.  We
 * additionally guard against SSR / non-browser contexts so the import is
 * safe to evaluate during Jest / node tooling.
 */
export function isSupported() {
  if (typeof window === 'undefined' || !window.PublicKeyCredential) return false;
  try {
    return client.isAvailable();
  } catch {
    return false;
  }
}

export default { create, get, isSupported };
